import { z } from 'zod'
import { AppError } from '../response'
import type { AppRecord } from '../types'
import { eurToCents } from './allocation'
import { invalidateSupplierResponsePricing } from '../supplierComparison/lifecycle'
import { assertSupplierProcessingComplete } from './processingCheckpoint'
import { requestCachedStore, freshInspection } from '../requestCachedStore'
import { readRecordsByIds, readSupplierResponseLines } from '../recordReads'
import { updateRecords } from './batchStore'
import type { AirtableClient } from '../airtableClient'

type Store = { get(entity: string, id: string): Promise<AppRecord>; list(entity: string): Promise<AppRecord[]>; update(entity: string, id: string, values: Record<string, unknown>): Promise<AppRecord> } & Partial<Pick<AirtableClient, 'listByIds' | 'updateMany'>>
type ResolutionEntry = {
  sourceSupplierQuoteItemId?: string
  sourceAmountEur?: number
  treatment?: string
  allocationMethod?: string
  autoAllocated?: boolean
  reviewRequired?: boolean
  allocations?: Array<{ targetSupplierQuoteItemId?: string; amountEur?: number }>
  override?: string
  reason?: string
}

const schemaVersion = 'termovista-supplier-response-v2'
export const SUPPLIER_RESPONSE_ENVELOPE_VERSION = 'termovista-supplier-response-v3'
const overrides = new Set(['overrideTransportCost', 'overrideMeasurementCost', 'overrideOtherQuoteCosts'])
const text = (v: unknown) => typeof v === 'string' ? v.trim() : ''

const dispositionBaseSchema = z.object({
  supplierQuoteItemId: z.string().regex(/^rec[a-zA-Z0-9]{14}$/),
  sourceLineKey: z.string().trim().max(500).nullable().optional(),
  sourceAmountEur: z.number().finite().nonnegative().nullable().optional(),
  ignoreInAssessment: z.boolean(),
  excludeFromCustomerQuote: z.boolean(),
  includedInSupplierTotal: z.boolean().nullable(),
  reason: z.string().trim().max(2000),
})
type DispositionSchemaValue = z.infer<typeof dispositionBaseSchema>
const dispositionSchema = dispositionBaseSchema.superRefine((value: DispositionSchemaValue, context: z.RefinementCtx) => {
  if (!value.ignoreInAssessment && value.includedInSupplierTotal !== null) context.addIssue({ code: z.ZodIssueCode.custom, path: ['includedInSupplierTotal'], message: 'Only assessment-ignored lines may specify printed-total inclusion.' })
  if (value.ignoreInAssessment && value.includedInSupplierTotal === null) context.addIssue({ code: z.ZodIssueCode.custom, path: ['includedInSupplierTotal'], message: 'Specify whether the printed total includes this line.' })
  if (!value.ignoreInAssessment && !value.excludeFromCustomerQuote) context.addIssue({ code: z.ZodIssueCode.custom, path: ['reason'], message: 'A line disposition must exclude assessment, customer pricing, or both.' })
  if ((value.ignoreInAssessment || value.excludeFromCustomerQuote) && !value.reason.trim()) context.addIssue({ code: z.ZodIssueCode.custom, path: ['reason'], message: 'Provide a reason for the line disposition.' })
})
export type SupplierLineDisposition = z.infer<typeof dispositionSchema>

function accessoryTargetAllowed(source: AppRecord, target: AppRecord, pricingItems: Map<string, AppRecord>) {
  const pricingItemId = text(target.pricingItemId)
  if (!pricingItemId) return false
  const item = pricingItems.get(pricingItemId)
  if (!item) return false
  const position = text(source.supplierPosition).replace(/\.$/, '').toUpperCase()
  if (position === 'MOLAREDE') return item.elementType === 'mosquito_net' && item.mosquitoNetType === 'roll' && item.mosquitoNetSpring === true
  // TRAV has no deterministic association: the reviewer may allocate it to
  // any requested Pricing Item, including multiple targets.
  if (position === 'TRAV') return true
  return true
}

export function parseSupplierResponseEnvelope(raw: unknown): Record<string, any> | null {
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw
    return value && typeof value === 'object' && [(value as any).schemaVersion === schemaVersion, (value as any).schemaVersion === SUPPLIER_RESPONSE_ENVELOPE_VERSION].some(Boolean) ? value as Record<string, any> : null
  } catch { return null }
}

function validLineAmount(line: AppRecord | undefined) {
  if (!line) return null
  const stated = typeof line.supplierLineCost === 'number' ? line.supplierLineCost : NaN
  if (Number.isFinite(stated) && stated >= 0) return Math.round(stated * 100) / 100
  const quantity = typeof line.supplierQuantity === 'number' ? line.supplierQuantity : NaN
  const unit = typeof line.supplierUnitCost === 'number' ? line.supplierUnitCost : NaN
  if (Number.isFinite(quantity) && quantity >= 0 && Number.isFinite(unit) && unit >= 0) return Math.round(quantity * unit * 100) / 100
  return null
}

function enrichDispositionSourceMetadata(raw: unknown, envelope: Record<string, any> | null) {
  if (!Array.isArray(raw) || !envelope || !envelope.sourceLineMap || typeof envelope.sourceLineMap !== 'object') return raw
  return raw.map((entry) => {
    if (!entry || typeof entry !== 'object') return entry
    const value = entry as Record<string, unknown>
    const source = (envelope.sourceLineMap as Record<string, unknown>)[String(value.supplierQuoteItemId)]
    if (!source || typeof source !== 'object') return entry
    const sourceRecord = source as Record<string, unknown>
    return {
      ...value,
      ...(value.sourceLineKey == null && typeof sourceRecord.sourceLineKey === 'string' ? { sourceLineKey: sourceRecord.sourceLineKey } : {}),
      ...(value.sourceAmountEur == null && typeof sourceRecord.sourceAmountEur === 'number' ? { sourceAmountEur: sourceRecord.sourceAmountEur } : {}),
    }
  })
}

export function parseLineDispositions(raw: unknown, currentIds?: Set<string>, currentLines?: AppRecord[]) {
  const values = Array.isArray(raw) ? raw : []
  const parsed = z.array(dispositionSchema).safeParse(values)
  if (!parsed.success) throw new AppError('Supplier line dispositions are invalid.', 422, 'SUPPLIER_RESPONSE_DISPOSITION_INVALID')
  const seen = new Set<string>()
  for (const value of parsed.data) {
    if (seen.has(value.supplierQuoteItemId)) throw new AppError('Each supplier response line may have only one disposition.', 409, 'SUPPLIER_RESPONSE_DISPOSITION_DUPLICATE')
    if (currentIds && !currentIds.has(value.supplierQuoteItemId)) throw new AppError('A supplier line disposition does not belong to this Quote.', 409, 'SUPPLIER_RESPONSE_LINE_QUOTE_MISMATCH')
    if (value.ignoreInAssessment && value.includedInSupplierTotal === true && currentLines) {
      const line = currentLines.find((candidate) => candidate.id === value.supplierQuoteItemId)
      if (validLineAmount(line) === null) throw new AppError('An assessment-ignored line marked as included in the supplier total must have a valid line amount.', 422, 'SUPPLIER_RESPONSE_DISPOSITION_AMOUNT_REQUIRED')
    }
    seen.add(value.supplierQuoteItemId)
  }
  return parsed.data
}

export function envelopeLineDispositions(envelope: Record<string, any> | null, currentIds?: Set<string>, currentLines?: AppRecord[]) {
  if (!envelope) return []
  try { return parseLineDispositions(envelope.lineDispositions, currentIds, currentLines) } catch { return [] }
}

/**
 * Parser proposals can outlive a Supplier Quote Item when a response is
 * corrected or a line is removed.  Keep those entries as audit metadata, but
 * do not let a deleted Airtable record prevent the remaining, current
 * response from being reviewed.
 */
function currentResolutionEntries(envelope: Record<string, any>, currentIds: Set<string>) {
  const items = Array.isArray(envelope.items) ? envelope.items : []
  const parserManagedIds = new Set(
    Array.isArray(envelope.parserManagedSupplierQuoteItemIds)
      ? envelope.parserManagedSupplierQuoteItemIds.map((id: unknown) => text(id)).filter(Boolean)
      : [],
  )
  const current: unknown[] = []
  const stale: unknown[] = []
  for (const item of items) {
    const sourceId = text(item && typeof item === 'object' ? (item as any).sourceSupplierQuoteItemId : '')
    if (sourceId && currentIds.has(sourceId)) current.push(item)
    else if (sourceId && parserManagedIds.has(sourceId)) stale.push(item)
    else current.push(item) // Let the strict validator report an unknown source.
  }
  return { current, stale }
}

function withStaleResolutionMetadata(envelope: Record<string, any>, stale: unknown[]) {
  if (!stale.length) return envelope
  const existing = Array.isArray(envelope.staleResolutionItems) ? envelope.staleResolutionItems : []
  const seen = new Set<string>()
  const merged = [...existing, ...stale].filter((item) => {
    const sourceId = text(item && typeof item === 'object' ? (item as any).sourceSupplierQuoteItemId : '')
    const key = sourceId || JSON.stringify(item)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  return { ...envelope, staleResolutionItems: merged }
}

function parseAmount(value: unknown, label: string) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) throw new AppError(`${label} must be a non-negative EUR amount.`, 422, 'SUPPLIER_RESPONSE_RESOLUTION_INVALID')
  return eurToCents(value)
}

/** Validates parser/manual allocations against the current Airtable lines without mutating anything. */
export async function validateSupplierResponseResolutions(store: Store, quoteId: string, entries: unknown[], options: { allowUnresolved?: boolean; quote?: AppRecord } = {}) {
  const lines = options.quote ? await readSupplierResponseLines(store, options.quote) : (await store.list('supplier-quote-items')).filter(line => line.quoteId === quoteId)
  const allPricingItems = await readRecordsByIds(store, 'pricing-items', lines.flatMap(line => typeof line.pricingItemId === 'string' ? [line.pricingItemId] : []))
  const byId = new Map(lines.map((line) => [line.id, line]))
  const pricingItems = new Map(allPricingItems.map((item) => [item.id, item]))
  const seen = new Set<string>()
  const normalized: ResolutionEntry[] = []
  for (const raw of entries) {
    if (!raw || typeof raw !== 'object') throw new AppError('Supplier response resolution is invalid.', 422, 'SUPPLIER_RESPONSE_RESOLUTION_INVALID')
    const entry = raw as ResolutionEntry
    const sourceId = text(entry.sourceSupplierQuoteItemId)
    const source = byId.get(sourceId)
    if (!source || seen.has(sourceId)) throw new AppError('Each supplier source line must be resolved exactly once.', 409, 'SUPPLIER_RESPONSE_RESOLUTION_INVALID')
    seen.add(sourceId)
    const sourceAmount = parseAmount(entry.sourceAmountEur ?? source.supplierLineCost, 'Source amount')
    const currentAmount = validLineAmount(source)
    if (currentAmount === null || sourceAmount !== eurToCents(currentAmount)) throw new AppError('Assigned costs no longer match the supplier source amount. Review the assignments again.', 409, 'SUPPLIER_RESPONSE_RESOLUTION_MISMATCH')
    const treatment = text(entry.treatment)
    if (!['allocated', 'covered_by_override', 'excluded', 'unresolved'].includes(treatment)) throw new AppError('Supplier response resolution treatment is invalid.', 422, 'SUPPLIER_RESPONSE_RESOLUTION_INVALID')
    if (treatment === 'allocated' && source.pricingItemId) throw new AppError('A supplier cost cannot be both a primary response and an allocated source.', 409, 'SUPPLIER_RESPONSE_RESOLUTION_INVALID')
    if (treatment === 'unresolved' && options.allowUnresolved !== true) throw new AppError('Resolve every unmatched supplier amount before marking Reviewed.', 409, 'SUPPLIER_RESPONSE_RESOLUTION_REQUIRED')
    const allocations = Array.isArray(entry.allocations) ? entry.allocations : []
    const allocationIds = new Set<string>()
    let allocatedCents = 0
    for (const allocation of allocations) {
      const targetId = text(allocation?.targetSupplierQuoteItemId)
      const target = byId.get(targetId)
      if (!target || allocationIds.has(targetId)) throw new AppError('Resolution targets must be Supplier Quote Items from this Quote.', 409, 'SUPPLIER_RESPONSE_RESOLUTION_INVALID')
      if (!accessoryTargetAllowed(source, target, pricingItems)) throw new AppError('The selected Pricing Item is not eligible for this supplier accessory.', 409, 'SUPPLIER_RESPONSE_RESOLUTION_TARGET_INVALID')
      allocationIds.add(targetId)
      allocatedCents += parseAmount(allocation?.amountEur, 'Allocation amount')
    }
    if (treatment === 'allocated' && allocatedCents !== sourceAmount) throw new AppError('Allocation amounts must reconcile to the exact source-line cents.', 409, 'SUPPLIER_RESPONSE_RESOLUTION_MISMATCH')
    if (treatment !== 'allocated' && allocations.length) throw new AppError('Only allocated resolutions may contain target allocations.', 422, 'SUPPLIER_RESPONSE_RESOLUTION_INVALID')
    if (treatment === 'covered_by_override' && !overrides.has(text(entry.override))) throw new AppError('Choose an override category for covered supplier costs.', 422, 'SUPPLIER_RESPONSE_RESOLUTION_INVALID')
    if (treatment === 'excluded' && !text(entry.reason)) throw new AppError('Provide a reason for excluded supplier costs.', 422, 'SUPPLIER_RESPONSE_RESOLUTION_INVALID')
    normalized.push({ ...entry, sourceSupplierQuoteItemId: sourceId, sourceAmountEur: sourceAmount / 100, allocations: allocations.map((a) => ({ targetSupplierQuoteItemId: text(a.targetSupplierQuoteItemId), amountEur: parseAmount(a.amountEur, 'Allocation amount') / 100 })) })
  }
  return normalized
}

export async function saveSupplierResponseResolutions(input: { store: Store; quoteId: string; entries: unknown[]; lineDispositions?: unknown[]; technicalMatchIds?: string[] }) {
  input = { ...input, store: requestCachedStore(input.store) }
  freshInspection(input.store)
  const quote = await input.store.get('quotes', input.quoteId)
  assertSupplierProcessingComplete(quote)
  const envelope = parseSupplierResponseEnvelope(quote.supplierCostResolutionJson)
  // Line dispositions also apply to manually entered responses, where there
  // may not be a parser envelope yet. Accessory allocations still require an
  // active envelope because their source-line proposals come from parsing.
  if (!envelope && input.lineDispositions === undefined) {
    throw new AppError('Process the active supplier PDF before editing allocations.', 409, 'SUPPLIER_RESPONSE_PROCESSING_REQUIRED')
  }
  const currentLines = await readSupplierResponseLines(input.store, quote)
  const currentIds = new Set(currentLines.map(line => line.id))
  const resolutionItems = currentResolutionEntries(envelope ?? { items: [] }, currentIds)
  const supplied = input.entries
  const flattened = supplied.length > 0 && supplied.every((entry) => entry && typeof entry === 'object' && 'targetSupplierQuoteItemId' in entry)
  const entriesInput = flattened
    ? resolutionItems.current.map((entry: any) => ({ ...entry, allocations: (supplied as any[]).filter((allocation) => String(allocation.sourceSupplierQuoteItemId) === String(entry.sourceSupplierQuoteItemId)).map((allocation) => ({ targetSupplierQuoteItemId: allocation.targetSupplierQuoteItemId, amountEur: allocation.amountEur })) }))
    : supplied.length ? supplied : resolutionItems.current
  const entries = await validateSupplierResponseResolutions(input.store, quote.id, entriesInput, { allowUnresolved: true, quote })
  const requestedMatches = new Set((input.technicalMatchIds || []).map(String))
  if (requestedMatches.size) {
    const linesById = new Map(currentLines.map(line => [line.id, line]))
    for (const lineId of requestedMatches) {
      const line = linesById.get(lineId)
      if (!line || !line.pricingItemId) throw new AppError('Only a supplier line linked to a requested Pricing Item can be accepted as a technical match.', 409, 'SUPPLIER_RESPONSE_RESOLUTION_INVALID')
    }
  }
  const dispositionInput = input.lineDispositions === undefined ? undefined : enrichDispositionSourceMetadata(input.lineDispositions, envelope)
  const lineDispositions = dispositionInput === undefined ? envelopeLineDispositions(envelope, currentIds, currentLines) : parseLineDispositions(dispositionInput, currentIds, currentLines)
  freshInspection(input.store)
  const current = await input.store.get('quotes', quote.id)
  assertSupplierProcessingComplete(current)
  if (current.supplierCostResolutionJson !== quote.supplierCostResolutionJson) throw new AppError('Supplier resolutions changed during save. Refresh and review them again.', 409, 'SUPPLIER_RESPONSE_REVIEW_CONFLICT')
  await validateSupplierResponseResolutions(input.store, current.id, entries, { allowUnresolved: true, quote: current })
  parseLineDispositions(lineDispositions, new Set((await readSupplierResponseLines(input.store, current)).map(line => line.id)), await readSupplierResponseLines(input.store, current))
  await invalidateSupplierResponsePricing(input.store, current)
  const next = withStaleResolutionMetadata({ ...(envelope ?? {}), schemaVersion: SUPPLIER_RESPONSE_ENVELOPE_VERSION, state: 'proposed', items: entries, lineDispositions }, resolutionItems.stale)
  await input.store.update('quotes', quote.id, { supplierCostResolutionJson: JSON.stringify(next), status: 'Pricing Review Required' })
  if (requestedMatches.size) {
    await updateRecords(input.store, 'supplier-quote-items', [...requestedMatches].map(id => ({ id, values: { supplierMatchStatus: 'matched', reviewRequired: false, notes: 'Technical difference explicitly accepted by reviewer.' } })))
  }
  return next
}

export async function acceptSupplierResponseResolutions(store: Store, quote: AppRecord) {
  assertSupplierProcessingComplete(quote)
  const envelope = parseSupplierResponseEnvelope(quote.supplierCostResolutionJson)
  if (!envelope) return false
  const lines = await readSupplierResponseLines(store, quote)
  const currentIds = new Set(lines.map(line => line.id))
  const resolutionItems = currentResolutionEntries(envelope, currentIds)
  const entries = await validateSupplierResponseResolutions(store, quote.id, resolutionItems.current, { allowUnresolved: false, quote })
  parseLineDispositions(envelope.lineDispositions, currentIds, lines)
  const unresolved = entries.filter((entry) => entry.treatment === 'unresolved')
  if (unresolved.length) throw new AppError('Resolve every unmatched supplier amount before marking Reviewed.', 409, 'SUPPLIER_RESPONSE_RESOLUTION_REQUIRED')
  const next = withStaleResolutionMetadata({ ...envelope, schemaVersion: SUPPLIER_RESPONSE_ENVELOPE_VERSION, state: 'accepted', items: entries }, resolutionItems.stale)
  await store.update('quotes', quote.id, { supplierCostResolutionJson: JSON.stringify(next) })
  return true
}

export function acceptedSupplierAllocations(quote: AppRecord) {
  const envelope = parseSupplierResponseEnvelope(quote.supplierCostResolutionJson)
  if (!envelope || envelope.state !== 'accepted') return new Map<string, number>()
  const customerExcluded = new Set((envelopeLineDispositions(envelope) as Array<DispositionSchemaValue>)
    .filter((disposition) => disposition.excludeFromCustomerQuote)
    .map((disposition) => disposition.supplierQuoteItemId))
  const totals = new Map<string, number>()
  for (const entry of Array.isArray(envelope.items) ? envelope.items as ResolutionEntry[] : []) {
    if (entry.treatment !== 'allocated') continue
    if (customerExcluded.has(text(entry.sourceSupplierQuoteItemId))) continue
    for (const allocation of entry.allocations || []) {
      const id = text(allocation.targetSupplierQuoteItemId)
      if (id) totals.set(id, (totals.get(id) || 0) + Number(allocation.amountEur || 0))
    }
  }
  return totals
}

export function acceptedSupplierResolutionIds(quote: AppRecord) {
  const envelope = parseSupplierResponseEnvelope(quote.supplierCostResolutionJson)
  if (!envelope || envelope.state !== 'accepted') return new Set<string>()
  const ids = new Set((Array.isArray(envelope.items) ? envelope.items : [])
    .filter((entry: ResolutionEntry) => ['allocated', 'covered_by_override', 'excluded'].includes(text(entry.treatment)))
    .map((entry: ResolutionEntry) => text(entry.sourceSupplierQuoteItemId)).filter(Boolean))
  for (const disposition of envelopeLineDispositions(envelope) as Array<DispositionSchemaValue>) {
    if (disposition.excludeFromCustomerQuote) ids.add(disposition.supplierQuoteItemId)
  }
  return ids
}

export function acceptedSupplierLineDisposition(quote: AppRecord, supplierQuoteItemId: string) {
  const envelope = parseSupplierResponseEnvelope(quote.supplierCostResolutionJson)
  if (!envelope || envelope.state !== 'accepted') return null
  return (envelopeLineDispositions(envelope) as Array<DispositionSchemaValue>).find((disposition) => disposition.supplierQuoteItemId === supplierQuoteItemId) ?? null
}
